import { useEffect } from 'react';
import type { FC } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  Link,
  Typography,
} from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ReceiptIcon from '@material-ui/icons/Receipt';
import useAuth from '../../hooks/useAuth';
import BriefcaseIcon from '../../icons/Briefcase';
import CalendarIcon from '../../icons/Calendar';
import ChartPieIcon from '../../icons/ChartPie';
import ChartSquareBarIcon from '../../icons/ChartSquareBar';
import ChatAltIcon from '../../icons/ChatAlt';
import ClipboardListIcon from '../../icons/ClipboardList';
import FolderOpenIcon from '../../icons/FolderOpen';
import MailIcon from '../../icons/Mail';
import ShareIcon from '../../icons/Share';
import ShoppingBagIcon from '../../icons/ShoppingBag';
import ShoppingCartIcon from '../../icons/ShoppingCart';
import UserIcon from '../../icons/User';
import UsersIcon from '../../icons/Users';
import Logo from '../Logo';
import NavSection from '../NavSection';
import Scrollbar from '../Scrollbar';

interface DashboardSidebarProps {
  onMobileClose: () => void;
  openMobile: boolean;
}

const sections = [
  {
    title: 'General',
    items: [
      {
        title: 'Overview',
        path: '/',
        icon: <ChartSquareBarIcon fontSize="small" />,
      },
      {
        title: 'Create Quote',
        path: '//create-quote',
        icon: <ReceiptIcon fontSize="small" />,
      },
    ],
  },
  {
    title: 'Management',
    items: [
      {
        title: 'Groups',
        path: '//groups',
        icon: <UsersIcon fontSize="small" />,
      },
    ],
  },
  // {
  //   title: 'Filters',
  //   items: [
  //     {
  //       title: 'Quoted',
  //       path: '//kanban',
  //       icon: <ClipboardListIcon fontSize="small" />,
  //     },
  //     {
  //       title: 'Paid',
  //       path: '//kanban',
  //       icon: <ClipboardListIcon fontSize="small" />,
  //     },
  //     {
  //       title: 'Archive',
  //       path: '//kanban',
  //       icon: <ClipboardListIcon fontSize="small" />,
  //     },
  //   ],
  // },
  // {
  //   title: 'Samples - Will delete later...',
  //   items: [
  //     {
  //       title: 'Analytics',
  //       path: '//analytics',
  //       icon: <ChartPieIcon fontSize="small" />,
  //     },
  //     {
  //       title: 'Finance',
  //       path: '//finance',
  //       icon: <ShoppingBagIcon fontSize="small" />,
  //     },
  //     {
  //       title: 'Account',
  //       path: '//account',
  //       icon: <UserIcon fontSize="small" />,
  //     },
  //     {
  //       title: 'Products',
  //       path: '//products',
  //       icon: <ShoppingCartIcon fontSize="small" />,
  //       children: [
  //         {
  //           title: 'List',
  //           path: '//products',
  //         },
  //         {
  //           title: 'Create',
  //           path: '//products/new',
  //         },
  //       ],
  //     },
  //     {
  //       title: 'Orders',
  //       icon: <FolderOpenIcon fontSize="small" />,
  //       path: '//orders',
  //       children: [
  //         {
  //           title: 'List',
  //           path: '//orders',
  //         },
  //         {
  //           title: 'Details',
  //           path: '//orders/1',
  //         },
  //       ],
  //     },
  //     {
  //       title: 'Invoices',
  //       path: '//invoices',
  //       icon: <ReceiptIcon fontSize="small" />,
  //       children: [
  //         {
  //           title: 'List',
  //           path: '//invoices',
  //         },
  //         {
  //           title: 'Details',
  //           path: '//invoices/1',
  //         },
  //       ],
  //     },
  //     {
  //       title: 'Projects',
  //       path: '//projects',
  //       icon: <BriefcaseIcon fontSize="small" />,
  //       children: [
  //         {
  //           title: 'Browse',
  //           path: '//projects/browse',
  //         },
  //         {
  //           title: 'Details',
  //           path: '//projects/1',
  //         },
  //         {
  //           title: 'Create',
  //           path: '//projects/new',
  //         },
  //       ],
  //     },
  //     {
  //       title: 'Social',
  //       path: '//social',
  //       icon: <ShareIcon fontSize="small" />,
  //       children: [
  //         {
  //           title: 'Profile',
  //           path: '//social/profile',
  //         },
  //         {
  //           title: 'Feed',
  //           path: '//social/feed',
  //         },
  //       ],
  //     },
  //     {
  //       title: 'Kanban',
  //       path: '//kanban',
  //       icon: <ClipboardListIcon fontSize="small" />,
  //     },
  //     {
  //       title: 'Mail',
  //       path: '//mail',
  //       icon: <MailIcon fontSize="small" />,
  //     },
  //     {
  //       title: 'Chat',
  //       path: '//chat',
  //       icon: <ChatAltIcon fontSize="small" />,
  //     },
  //     {
  //       title: 'Calendar',
  //       path: '//calendar',
  //       icon: <CalendarIcon fontSize="small" />,
  //     },
  //   ],
  // },
];

const DashboardSidebar: FC<DashboardSidebarProps> = (props) => {
  const { onMobileClose, openMobile } = props;
  const location = useLocation();
  const { user } = useAuth();
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Scrollbar options={{ suppressScrollX: true }}>
        <Box
          sx={{
            display: {
              lg: 'none',
              xs: 'flex',
            },
            justifyContent: 'center',
            p: 2,
          }}
        >
          <RouterLink to="/">
            <Logo
              sx={{
                height: 40,
                width: 40,
              }}
            />
          </RouterLink>
        </Box>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: 'background.default',
              borderRadius: 1,
              display: 'flex',
              overflow: 'hidden',
              p: 2,
            }}
          >
            <RouterLink to="/dashboard/account">
              <Avatar
                src={user.avatar}
                sx={{
                  cursor: 'pointer',
                  height: 48,
                  width: 48,
                }}
              />
            </RouterLink>
            <Box sx={{ ml: 2 }}>
              <Typography color="textPrimary" variant="subtitle2">
                {user.name}
              </Typography>
              <Typography variant="body2">
                Role: {/* {user.role} TODO://UPDATE ME*/}
                <span style={{ color: '#03A0EF' }}>Admin</span>
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider />
        <Box sx={{ p: 2 }}>
          {sections.map((section) => (
            <NavSection
              key={section.title}
              pathname={location.pathname}
              sx={{
                '& + &': {
                  mt: 3,
                },
              }}
              {...section}
            />
          ))}
        </Box>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography color="textPrimary" variant="subtitle2">
            Quicklinks
          </Typography>
          <Button
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            href="https://bido.admin.datocms.com/"
            variant="contained"
            target="_blank"
          >
            CMS
          </Button>
          <Button
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            href="https://calendly.com/event_types/user/me"
            variant="contained"
            target="_blank"
          >
            Calendly
          </Button>
          <Button
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            href="https://www.bidotravel.com/"
            variant="contained"
            target="_blank"
          >
            Wix Website
          </Button>
        </Box>
      </Scrollbar>
    </Box>
  );

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            height: 'calc(100% - 64px) !important',
            top: '64px !Important',
            width: 280,
          },
        }}
        variant="permanent"
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="left"
      onClose={onMobileClose}
      open={openMobile}
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          width: 280,
        },
      }}
      variant="temporary"
    >
      {content}
    </Drawer>
  );
};

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default DashboardSidebar;
